import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import { useSettings } from '../context/SettingsContext';
import { LoadingState } from '../components/Loading';
import { FaSearch, FaFilter, FaTimes, FaStar, FaShoppingBag } from 'react-icons/fa';
import './Products.css';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0 });

  const filters = {
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    sort: searchParams.get('sort') || '-createdAt',
    featured: searchParams.get('featured') || '',
    page: parseInt(searchParams.get('page') || '1'),
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadProducts(); }, [searchParams]);

  const loadCategories = async () => {
    try {
      const res = await axios.get('/api/categories');
      setCategories(res.data);
    } catch (e) { console.error(e); }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.featured) params.append('featured', filters.featured);
      params.append('page', filters.page);
      params.append('limit', 12);

      const res = await axios.get(`/api/products?${params}`);
      setProducts(res.data.products);
      setPagination({
        currentPage: parseInt(res.data.currentPage),
        totalPages: res.data.totalPages,
        total: res.data.total,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    if (key !== 'page') newParams.delete('page');
    setSearchParams(newParams);
  };

  const clearFilters = () => setSearchParams({});

  const ProductCard = ({ product }) => {
    const discount = product.comparePrice && product.comparePrice > product.price
      ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
      : 0;

    return (
      <Link to={`/products/${product._id}`} className="product-card">
        <div className="product-image">
          {product.images && product.images[0] ? (
            <img src={product.images[0].url} alt={product.name} loading="lazy" />
          ) : (
            <div className="no-image">No Image</div>
          )}
          {discount > 0 && <span className="product-discount">-{discount}%</span>}
          {product.stock === 0 && <span className="product-out">Out of stock</span>}
        </div>
        <div className="product-info">
          <h3>{product.name}</h3>
          {product.rating?.average > 0 && (
            <div className="product-rating">
              <FaStar /> {product.rating.average.toFixed(1)}
            </div>
          )}
          <div className="product-price-row">
            <span className="product-price">{formatCurrency(product.price, settings?.currency)}</span>
            {product.comparePrice > product.price && (
              <span className="product-compare">{formatCurrency(product.comparePrice, settings?.currency)}</span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  const activeFilterCount = [filters.category, filters.search, filters.featured].filter(Boolean).length;

  return (
    <div className="products-page page">
      <div className="container">
        <div className="page-header">
          <div>
            <h1>Shop</h1>
            <p className="page-subtitle">
              {loading ? 'Loading products...' : `${pagination.total} ${pagination.total === 1 ? 'product' : 'products'} available`}
            </p>
          </div>

          <div className="page-toolbar">
            <button
              className="btn btn-secondary mobile-only"
              onClick={() => setFiltersOpen(true)}
            >
              <FaFilter /> Filters {activeFilterCount > 0 && <span className="count-badge">{activeFilterCount}</span>}
            </button>

            <select
              value={filters.sort}
              onChange={(e) => updateFilter('sort', e.target.value)}
              className="sort-select"
            >
              <option value="-createdAt">Newest first</option>
              <option value="price-asc">Price: low to high</option>
              <option value="price-desc">Price: high to low</option>
              <option value="name">Name: A-Z</option>
            </select>
          </div>
        </div>

        <div className="products-layout">
          {/* Sidebar Filters */}
          <aside className={`filters-sidebar ${filtersOpen ? 'open' : ''}`}>
            <div className="filters-header">
              <h3>Filters</h3>
              <button className="mobile-only icon-btn" onClick={() => setFiltersOpen(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="filter-group">
              <label>Search</label>
              <div className="search-input-wrapper">
                <FaSearch />
                <input
                  type="search"
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                />
              </div>
            </div>

            <div className="filter-group">
              <label>Category</label>
              <div className="filter-options">
                <button
                  className={`filter-option ${!filters.category ? 'active' : ''}`}
                  onClick={() => updateFilter('category', '')}
                >
                  All categories
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat._id}
                    className={`filter-option ${filters.category === cat._id ? 'active' : ''}`}
                    onClick={() => updateFilter('category', cat._id)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="filter-group">
              <label>Show only</label>
              <div className="filter-options">
                <button
                  className={`filter-option ${filters.featured === 'true' ? 'active' : ''}`}
                  onClick={() => updateFilter('featured', filters.featured === 'true' ? '' : 'true')}
                >
                  Featured products
                </button>
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={clearFilters}>
                <FaTimes /> Clear all filters
              </button>
            )}
          </aside>

          {filtersOpen && <div className="mobile-overlay" onClick={() => setFiltersOpen(false)} />}

          {/* Products grid */}
          <div className="products-content">
            {loading ? (
              <LoadingState message="Loading products..." />
            ) : products.length === 0 ? (
              <div className="empty-state">
                <FaShoppingBag className="empty-state-icon" />
                <h3>No products found</h3>
                <p>Try adjusting your filters or search term.</p>
                {activeFilterCount > 0 && (
                  <button className="btn btn-primary" onClick={clearFilters}>
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="products-grid">
                  {products.map((product) => (
                    <ProductCard key={product._id} product={product} />
                  ))}
                </div>

                {pagination.totalPages > 1 && (
                  <div className="pagination">
                    <button
                      disabled={pagination.currentPage === 1}
                      onClick={() => updateFilter('page', pagination.currentPage - 1)}
                      className="btn btn-secondary btn-sm"
                    >
                      Previous
                    </button>
                    <span className="page-info">
                      Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <button
                      disabled={pagination.currentPage === pagination.totalPages}
                      onClick={() => updateFilter('page', pagination.currentPage + 1)}
                      className="btn btn-secondary btn-sm"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
